import type { Transaction } from '@api/hooks/types/getTransactions';
import { Chip, Typography } from '@gurupay/gp-ui-library';
import { formatNumberAmount } from '@utils/utils';
import { isFuture, isToday, parseISO } from 'date-fns';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface TransactionsBreakdownProps {
	selection: Transaction[];
}

export const TransactionsBreakdown: FC<TransactionsBreakdownProps> = ({ selection }) => {
	const { t } = useTranslation('', { keyPrefix: 'signPayments.breakdown' });

	const data = useMemo(() => {
		const todayItems = selection.filter((tx) => {
			if (!tx.execution_date) return true;
			const date = parseISO(tx.execution_date);
			return isToday(date) || !isFuture(date);
		});
		const futureItems = selection.filter((tx) => {
			if (!tx.execution_date) return false;
			const date = parseISO(tx.execution_date);
			return !isToday(date) && isFuture(date);
		});

		return {
			today: {
				count: todayItems.length,
				amount: todayItems.reduce((sum, tx) => sum + tx.amount, 0),
			},
			future: {
				count: futureItems.length,
				amount: futureItems.reduce((sum, tx) => sum + tx.amount, 0),
			},
			total: {
				count: selection.length,
				amount: selection.reduce((sum, tx) => sum + tx.amount, 0),
			},
		};
	}, [selection]);

	return (
		<div className="flex flex-col gap-space-8">
			<Typography level="h2">{t('title')}</Typography>

			<div className="border border-neutral-100 rounded-lg p-space-8 w-full max-w-125 overflow-x-auto">
				<table className="w-full border-collapse">
					<thead>
						<tr>
							<th className="h-10 px-space-16 text-left text-sm-medium text-neutral-600 whitespace-nowrap border-b border-neutral-100">
								{t('scheduled')}
							</th>
							<th className="h-10 px-space-16 text-right text-sm-medium text-neutral-600 whitespace-nowrap border-b border-neutral-100">
								{t('count')}
							</th>
							<th className="h-10 px-space-16 text-right text-sm-medium text-neutral-600 whitespace-nowrap border-b border-neutral-100">
								{t('amount')}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td className="h-13 px-space-16 border-b border-neutral-100">
								<Chip
									color="primary"
									label={t('today')}
									size="small"
									variant="outlined"
								/>
							</td>
							<td className="h-13 px-space-16 text-right text-sm-bold text-neutral-950 border-b border-neutral-100">
								{data.today.count}
							</td>
							<td className="h-13 px-space-16 text-right text-sm-bold text-neutral-950 whitespace-nowrap border-b border-neutral-100">
								{formatNumberAmount(data.today.amount.toFixed(2))}
							</td>
						</tr>
						<tr>
							<td className="h-13 px-space-16 border-b border-neutral-950">
								<Chip
									color="primary"
									label={t('future')}
									size="small"
									variant="outlined"
								/>
							</td>
							<td className="h-13 px-space-16 text-right text-sm-bold text-neutral-950 border-b border-neutral-950">
								{data.future.count}
							</td>
							<td className="h-13 px-space-16 text-right text-sm-bold text-neutral-950 whitespace-nowrap border-b border-neutral-950">
								{formatNumberAmount(data.future.amount.toFixed(2))}
							</td>
						</tr>
					</tbody>
					<tfoot>
						<tr className="bg-neutral-50 text-base-bold text-neutral-950">
							<td className="h-14 px-space-16">{t('total')}</td>
							<td className="h-14 px-space-16 text-right">{data.total.count}</td>
							<td className="h-14 px-space-16 text-right whitespace-nowrap">
								{formatNumberAmount(data.total.amount.toFixed(2))}
							</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</div>
	);
};
